/**
 * What the signed-in person may do in Human Resources, and the "view as"
 * preview.
 *
 * The keys come from the platform (the principal's permissions). The preview
 * lets somebody look at the suite the way a lower level sees it — an HR
 * officer's screens, an assistant's — and it can only take keys away, never
 * add one. The platform enforces the keys on every request whatever this says.
 */
import { useSyncExternalStore } from "react";
import { hasPermission } from "../../api/dataSource";
import { useSession } from "../../app/SessionContext";
import {
  canOf,
  HR_KEYS,
  levelByKey,
  levelOfKeys,
  type HrCan,
  type HrLevel,
  type HrLevelKey,
} from "../../domain/hrAccess";

const KEY = "sky.hrViewAs";
const listeners = new Set<() => void>();

function read(): HrLevelKey | null {
  try {
    return (localStorage.getItem(KEY) as HrLevelKey | null) || null;
  } catch {
    return null;
  }
}

export function setViewAs(level: HrLevelKey | null) {
  try {
    if (level) localStorage.setItem(KEY, level);
    else localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable: the preview lasts as long as the page */
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export interface HrAccess {
  can: HrCan;
  /** the level the platform granted */
  granted: HrLevel;
  /** the level the screens are showing (a preview, when set) */
  level: HrLevel;
  previewing: boolean;
}

export function useHrAccess(): HrAccess {
  const { principal } = useSession();
  const viewAs = useSyncExternalStore(subscribe, read, () => null);
  const held = HR_KEYS.filter((k) => hasPermission(principal, k));
  const granted = levelOfKeys(held);
  const preview = viewAs ? levelByKey(viewAs) : null;
  const keys = preview ? held.filter((k) => preview.keys.includes(k)) : held;
  return { can: canOf(keys), granted, level: preview ? levelOfKeys(keys) : granted, previewing: !!preview };
}
